import React, { useState, useEffect, useMemo } from "react";
import { Typography, Form, Select, Flex, Button, Card } from "antd";
import moment from "moment";
import MasterComponent from "../../../components/Forms";
import { MasterConfigs } from "../../../interfaces/entity";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import { constants } from "../../../constants/common";
import DrawerComponent from "../../../components/Forms/Drawer";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import AccountDetails from "./AccountDetails";
import { createActivitiesColumn } from "../../../utils/activitiesColumn";
const { Text, Title } = Typography;

const LeadAccounts = () => {
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [selectedAccount, setSelectedAccount] = React.useState(null);
    const [selectedID, setSelectedID] = React.useState(null);
    const dispatch = useDispatch();

    // States for upcoming activities
    const [upcomingActivities, setUpcomingActivities] = useState<{ [key: string]: { date: string | Date; hasUpcoming: boolean } }>({});
    const { refreshTable, user } = useSelector((app: any) => app?.app || {});
    const [tableData, setTableData] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const isAdmin = user?.designation === 'Admin';

    // Refresh upcoming activities when table refreshes
    useEffect(() => {
        if (refreshTable && tableData.length > 0) {
            extractUpcomingActivitiesFromRecords(tableData);
        }
    }, [refreshTable, tableData.length]);

    // Extract upcoming activities info from accounts API response
    const extractUpcomingActivitiesFromRecords = (records: any[]) => {
        if (!records || records.length === 0) return;

        const activitiesMap: { [key: string]: { date: string | Date | null; hasUpcoming: boolean } } = {};

        records.forEach((record) => {
            if (!record?._id) return;
            const recordKey = `accounts-${record._id}`;

            // Use upcomingActivity from API response if available
            if (record.upcomingActivity) {
                activitiesMap[recordKey] = {
                    date: record.upcomingActivity.upcomingDate || null,
                    hasUpcoming: record.upcomingActivity.hasUpcoming || false
                };
            } else {
                // Default to no upcoming activities if not provided
                activitiesMap[recordKey] = {
                    date: null,
                    hasUpcoming: false
                };
            }
        });

        setUpcomingActivities(prev => ({
            ...prev,
            ...activitiesMap
        }));
    };

    const config: MasterConfigs = useMemo(() => {
        return {
            api: () => '/accounts',
            queryParams: {
                andOr: 'and', // Use 'and' to combine filters
            },
            title: 'Accounts',
            setInitialForm: () => {
                // For normal users, set default businessOwner to current user when creating new account
                if (!isAdmin && user?._id) {
                    return { businessOwner: user._id };
                }
                return {};
            },
            activitiesColumn: {
                enabled: true,
                modelName: 'accounts',
                title: 'Activities',
                width: 120,
                fixed: 'left',
                align: 'center',
                color: 'orange'
            },
            onDataLoad: (data: any) => {
                // Store table data and extract upcoming activities info from API response
                const records = Array.isArray(data) ? data : (data?.data || []);
                if (records && Array.isArray(records) && records.length > 0) {
                    setTableData(records);
                    extractUpcomingActivitiesFromRecords(records);
                }
            },
            onSummaryLoad: (summaryData: any) => {
                setSummary(summaryData);
            },
            renderSummary: () => {
                if (!summary) return null;
                return (
                    <Flex gap={12} wrap="wrap" style={{ width: '100%', marginBottom: 16 }}>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #1890ff' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Total Accounts:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.total || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #52c41a' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Today:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.today || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #faad14' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Last Week:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.lastWeek || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #722ed1' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Last Month:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.lastMonth || 0}
                                </Title>
                            </Flex>
                        </Card>
                    </Flex>
                );
            },
            getFilterColumns: () => {
                const filterColumns: any[] = [
                    {
                        label: 'Rating',
                        name: 'rating',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Rating',
                        },
                        children: constants.RATINGS.map(rating => ({
                            name: rating,
                            value: rating
                        }))
                    },
                    {
                        label: 'Account Type',
                        name: 'businessType',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Account Type',
                            showSearch: true,
                        },
                        children: constants.ACCOUNT_TYPES.map(type => ({
                            name: type,
                            value: type
                        }))
                    },
                    {
                        label: 'Industry',
                        name: 'industry',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Industry',
                            showSearch: true,
                        },
                        children: constants.INDUSTRIES.map(industry => ({
                            name: industry,
                            value: industry
                        }))
                    },
                ];

                // Business Owner filter - only visible to Admin
                if (isAdmin) {
                    filterColumns.push({
                        label: 'Account Owner',
                        name: 'businessOwner',
                        type: 'select',
                        isCombo: true,
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Account Owner',
                            showSearch: true,
                        },
                    });
                }

                filterColumns.push({
                    label: 'Parent Business',
                    name: 'parentBusiness',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Parent Business',
                        showSearch: true,
                    },
                });

                filterColumns.push({
                    label: 'Account Name',
                    name: 'businessName',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by account name',
                });

                filterColumns.push({
                    label: 'Phone',
                    name: 'phone',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by phone',
                });

                // Date range filters for created date
                filterColumns.push({
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select start date',
                });
                filterColumns.push({
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select end date',
                });

                return filterColumns;
            },
            modalTitle: (text) => text + ' Account',
            isActionTab: true,
            getColumns: () => {
                const columns: any[] = [];

                // Add Activities column if enabled in config
                const activitiesColumn = createActivitiesColumn(config.activitiesColumn);
                if (activitiesColumn) {
                    columns.push(activitiesColumn);
                }

                // Add other columns
                columns.push(
                    {
                        title: 'Account Name',
                        dataIndex: 'businessName',
                        key: 'businessName',
                        width: 200,
                        fixed: 'left',
                        render: (text, record) => <div onClick={() => handleLeadClick(record)}>{text}</div>
                    },
                    {
                        width: 150,
                        title: 'Phone',
                        dataIndex: 'phone',
                        key: 'phone',
                    },
                    {
                        width: 150,
                        title: 'Website',
                        dataIndex: 'website',
                        key: 'website',
                    },
                    {
                        title: 'Account Owner',
                        dataIndex: 'businessOwner',
                        key: 'businessOwner',
                        render: (text) => <SmallProfileCard
                            name={constants.GET_FULL_NAME(text)}
                            avatar={text?.avatar ? text?.avatar : undefined}
                            subTitle={text?.employeeID || text?.workEmail}
                        />,
                        width: 150
                    },
                    {
                        title: 'Created At',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        render: (text) => moment(text).format('DD MMM YYYY HH:mm'),
                        align: 'center',
                        width: 150,
                    }
                );

                return columns;
            },
            getFormColumns: () => [
                {
                    label: 'Account Image',
                    name: 'profilePhoto',
                    type: 'file',
                    uploadProps: {
                        accept: 'image/*',
                        maxCount: 1,
                        listType: 'picture-circle',
                    }
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Account Information</Title>
                },
                {
                    label: 'Account Owner',
                    name: 'businessOwner',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                {
                    label: 'Rating',
                    name: 'rating',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                        optionFilterProp: 'children',
                    },
                    children: constants.RATINGS.map(rating => ({ name: rating, value: rating })),
                },
                {
                    label: 'Account Name',
                    name: 'businessName',
                    type: 'text',
                    rules: [{ required: true, message: 'Account Name is required' }],
                    span: 12,
                },
                {
                    label: 'Phone',
                    name: 'phone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Account Site',
                    name: 'businessSite',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
                                return urlRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid website URL'));
                            },
                        },
                    ],
                },
                {
                    label: 'Fax',
                    name: 'fax',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const faxRegex = /^[0-9+\-().\s]{6,20}$/;
                                return faxRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid fax number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Parent Business',
                    name: 'parentBusiness',
                    type: 'select',
                    span: 12,
                    isCombo: true,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                    },
                },
                {
                    label: 'Website',
                    name: 'website',
                    type: 'text',
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    new URL(value);
                                    return Promise.resolve();
                                } catch {
                                    return Promise.reject(new Error('Enter a valid website URL'));
                                }
                            },
                        },
                    ],
                    span: 12,
                },
                {
                    label: 'Account Number',
                    name: 'businessNumber',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const businessNumberRegex = /^[0-9]{1,10}$/;
                                return businessNumberRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid business number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Ticker Symbol',
                    name: 'tickerSymbol',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const tickerSymbolRegex = /^[A-Z]{1,5}$/;
                                return tickerSymbolRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid ticker symbol'));
                            },
                        },
                    ],
                },
                {
                    label: 'Account Type',
                    name: 'businessType',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.ACCOUNT_TYPES.map(type => ({ name: type, value: type })),
                },
                {
                    label: 'Ownership',
                    name: 'ownerShip',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.ACCOUNT_OWNERSHIP.map(ownership => ({ name: ownership, value: ownership })),
                },
                {
                    label: 'Industry',
                    name: 'industry',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.INDUSTRIES.map(industry => ({ name: industry, value: industry })),
                },
                {
                    label: 'Employee Count',
                    name: 'noOfEmployees',
                    type: 'number',
                    span: 12,
                },
                {
                    label: 'Annual Revenue',
                    name: 'annualRevenue',
                    type: 'text',
                    inputProps: {
                        prefix: 'INR ',
                    },
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const revenueRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                                return revenueRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid annual revenue'));
                            },
                        },
                    ],
                    span: 12,
                },
                {
                    label: 'SIC Code',
                    name: 'sicCode',
                    type: 'text',
                    span: 12
                },
                {
                    label: 'LinkedIn Profile URL',
                    name: 'linkedinProfileUrl',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    const url = new URL(value);
                                    if (url.hostname.includes('linkedin.com')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                } catch {
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                }
                            },
                        },
                    ],
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Billing Address Information</Title>
                },
                {
                    label: 'Street',
                    name: ['address', 'billing', 'street'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'City',
                    name: ['address', 'billing', 'city'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'State',
                    name: ['address', 'billing', 'state'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Zip Code',
                    name: ['address', 'billing', 'zipCode'],
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const zipRegex = /^[0-9]{4,10}$/;
                                return zipRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid zip code'));
                            },
                        },
                    ],
                },
                {
                    label: 'Country',
                    name: ['address', 'billing', 'country'],
                    type: 'text',
                    span: 12,
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Shipping Address Information</Title>
                },
                {
                    label: 'Street',
                    name: ['address', 'shipping', 'street'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'City',
                    name: ['address', 'shipping', 'city'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'State',
                    name: ['address', 'shipping', 'state'],
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Zip Code',
                    name: ['address', 'shipping', 'zipCode'],
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const zipRegex = /^[0-9]{4,10}$/;
                                return zipRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid zip code'));
                            },
                        },
                    ],
                },
                {
                    label: 'Country',
                    name: ['address', 'shipping', 'country'],
                    type: 'text',
                    span: 12,
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
                },
                {
                    label: 'Description',
                    name: 'description',
                    type: 'textarea',
                    span: 24,
                    textAreaProps: {
                        autoSize: { minRows: 3, maxRows: 6 },
                    }
                },
            ],
            combo: [
                // Business Owner combo - always include, but for normal users, default to current user
                {
                    api: '/users',
                    name: 'businessOwner',
                    idKey: '_id',
                    render: (text) => <ProfileCard
                        size={30}
                        name={constants.GET_FULL_NAME(text)}
                        avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                        role={null}
                    />
                },
                {
                    api: '/accounts',
                    name: 'parentBusiness',
                    idKey: '_id',
                    render: (text) => text?.businessName || 'N/A',
                }
            ],
            actions: [
                {
                    actionType: 'filter',
                    name: 'Filter',
                },
                {
                    name: 'Add Account',
                    actionType: 'Add',
                }
            ]
        };
    }, [summary, isAdmin]);

    const handleLeadClick = (record) => {
        setSelectedAccount(record);
        setOpenDrawer(true);
    }

    const handleEditClick = (record) => {
        setSelectedID(record?._id);
        setOpenDrawer(true);
    }

    return (
        <>
            <MasterComponent config={config} isModal={true} recordId={selectedID} />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={selectedAccount?.businessName || 'Account Details'}
                            avatarUrl={selectedAccount?.profilePhoto ? process.env.FILE_URL + selectedAccount?.profilePhoto : null}
                            role={selectedAccount?.title || 'Contact Details'}
                        />
                        {!selectedAccount?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => handleEditClick(selectedAccount)} />
                        </div>}
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%', style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <AccountDetails account={selectedAccount} />
            </DrawerComponent>
        </>
    );
}

export default LeadAccounts;